import React, { useContext, useState, useEffect, useMemo } from "react";
import "./Admin.css";
import { MdDelete, MdSwitchAccount, MdOutlineArchive, MdOutlineUnarchive } from "react-icons/md";
import { EtatGlobalContext } from "../EtatGlobal.js";
import Create from "./Create";

import { HiOutlineUserGroup, HiOutlineArrowDownTray } from "react-icons/hi2";
import { IoSearchOutline, IoOpenOutline } from "react-icons/io5";

import Hierarchie from "./Hierarchie.js";
import Cabinets from "./Cabinets.js";
import IdentifiantsPA from "./IdentifiantsPA.js";
import ImportMEG from "./ImportMEG.js";
import EvaInstructions from "./EvaInstructions.js";
import ParametresKycLab from "./ParametresKycLab.js";
import UserDetailModal from "./UserDetailModal.js";
import { MODULE_KEYS, getFunctionalityIcon, POLE_LABELS, defaultPoleForUser } from "./moduleIcons.js";
import { authFetch } from '../../HttpClient';
import { showStatusToast } from "../../components/ui/Toast";

import { dialog } from "../../components/ui/Dialog";
import { normalizeText } from "../../utils/text.js";

// Collaborateur sorti : compte conservé (historiques), connexion bloquée.
const STATUT_ARCHIVE = "Archivé";
const estArchive = (u) => u?.status === STATUT_ARCHIVE;

function Admin({ initialView, navKey }) {
  const { users, setUsers, isLoading, utilisateur, impersonateUser } =
    useContext(EtatGlobalContext);
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showCabinets, setShowCabinets] = useState(initialView === "cabinets");
  const [showIdentifiantsPA, setShowIdentifiantsPA] = useState(
    initialView === "identifiants-pa"
  );
  const [showImportMEG, setShowImportMEG] = useState(initialView === "import-meg");
  const [showEvaInstructions, setShowEvaInstructions] = useState(
    initialView === "eva-instructions"
  );
  const [showKycLab, setShowKycLab] = useState(initialView === "kyc-lab");
  const [search, setSearch] = useState("");
  const [cabinetFilter, setCabinetFilter] = useState("all");

  // Resynchronise la sous-vue quand Admin est déjà monté et qu'on revient
  // via le menu Paramétrage (ex: organigramme/cabinets -> collaborateurs sans démonter Admin).
  // navKey s'incrémente à chaque clic du menu, donc l'effet se redéclenche même
  // quand initialView ne change pas de valeur (ex: organigramme -> collaborateurs, les deux "null").
  useEffect(() => {
    setShowCabinets(initialView === "cabinets");
    setShowIdentifiantsPA(initialView === "identifiants-pa");
    setShowImportMEG(initialView === "import-meg");
    setShowEvaInstructions(initialView === "eva-instructions");
    setShowKycLab(initialView === "kyc-lab");
    setShowHierarchy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKey]);

  const cabinetOptions = useMemo(() => {
    const map = new Map();
    users.forEach((u) =>
      (u.userCabinets || []).forEach((uc) => {
        if (uc.cabinet) map.set(uc.cabinet.id, uc.cabinet);
      })
    );
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = normalizeText(search);
    return users.filter((u) => {
      const matchesSearch =
        !term ||
        [u.name, u.lastName, u.email]
          .filter(Boolean)
          .some((field) => normalizeText(field).includes(term));
      const matchesCabinet =
        cabinetFilter === "all" ||
        (u.userCabinets || []).some(
          (uc) => uc.cabinet?.id === Number(cabinetFilter)
        );
      return matchesSearch && matchesCabinet;
    }).sort((a, b) => Number(estArchive(a)) - Number(estArchive(b)));
  }, [users, search, cabinetFilter]);

  const handleExportUsers = () => {
    const header = ["Code", "Nom", "Prénom", "Mail", "Ligne", "Profil"];
    const rows = filteredUsers.map((u) => [
      `${u.name?.[0] || ""}${u.lastName?.[0] || ""}`,
      u.name,
      u.lastName,
      u.email,
      u.phone || "",
      u.poste || u.role,
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(";")
      )
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "collaborateurs.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleUpdateUserInList = (updatedUser) => {
    setUsers((prevUsers) =>
      prevUsers
        .map((user) => (user.id === updatedUser.id ? updatedUser : user))
        .sort((a, b) => a.id - b.id)
    );
  };

  const handleToggleArchive = async (user) => {
    const archive = !estArchive(user);
    const nom = `${user.lastName || ""} ${user.name || ""}`.trim();
    if (
      archive &&
      !(await dialog.confirm(
        `Archiver ${nom} ? Son accès à MyAlfa sera coupé immédiatement. Son nom reste visible dans les historiques, dossiers et tâches.`
      ))
    )
      return;
    const statutAvant = user.status;
    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, status: archive ? STATUT_ARCHIVE : "Actif" } : u))
    );
    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/users/${user.id}/archive`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archive }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      showStatusToast({
        status: "success",
        titre: archive ? "Collaborateur archivé" : "Collaborateur réactivé",
        message: archive ? `${nom} ne peut plus se connecter.` : `${nom} peut de nouveau se connecter.`,
      });
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: statutAvant } : u)));
      showStatusToast({ status: "error", titre: "Action non enregistrée", message: err.message });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (await dialog.confirm("Voulez-vous vraiment supprimer cet utilisateur ?")) {
      authFetch(`https://backend-myalfa.vercel.app/api/users/${userId}`, {
        method: "DELETE",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Échec de la suppression de l'utilisateur.");
          }
          return authFetch("https://backend-myalfa.vercel.app/api/users");
        })
        .then((response) => response.json())
        .then((data) => setUsers(data.sort((a, b) => a.id - b.id)))
        .catch((error) =>
          console.error("Erreur lors de la suppression :", error)
        );
    }
  };

  return (
    <div>
      {showHierarchy ? (
        <>
          <Hierarchie onCancel={() => setShowHierarchy(false)} />
        </>
      ) : showCabinets ? (
        <>
          <Cabinets onCancel={() => setShowCabinets(false)} />
        </>
      ) : showIdentifiantsPA ? (
        <>
          <IdentifiantsPA onCancel={() => setShowIdentifiantsPA(false)} />
        </>
      ) : showImportMEG ? (
        <>
          <ImportMEG onCancel={() => setShowImportMEG(false)} />
        </>
      ) : showEvaInstructions ? (
        <>
          <EvaInstructions onCancel={() => setShowEvaInstructions(false)} />
        </>
      ) : showKycLab ? (
        <ParametresKycLab onCancel={() => setShowKycLab(false)} />
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">Liste des collaborateurs</h1>
            <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">
              {users.length}
            </span>
          </div>
          <p className="text-sm text-gray-400 mb-6">
            Annuaire interne et droits d'accès aux modules
          </p>

          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative w-full max-w-md">
                <IoSearchOutline
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un collaborateur..."
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                />
              </div>
              <select
                value={cabinetFilter}
                onChange={(e) => setCabinetFilter(e.target.value)}
                className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
              >
                <option value="all">Tous les cabinets</option>
                {cabinetOptions.map((cab) => (
                  <option key={cab.id} value={cab.id}>
                    {cab.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => setShowHierarchy(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <HiOutlineUserGroup size={16} />
                Organigramme
              </button>
              <button
                onClick={handleExportUsers}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <HiOutlineArrowDownTray size={16} />
                Exporter
              </button>
              <button
                onClick={() => setShowCreatePage(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors"
              >
                + Ajouter un collaborateur
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-10">
              {users.length === 0
                ? "Aucun collaborateur enregistré."
                : "Aucun collaborateur trouvé."}
            </p>
          ) : (
            <div className="bg-white border border-gray-100 rounded-2xl overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#fff6ee] text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    <th className="px-5 py-3">Code</th>
                    <th className="px-5 py-3">Nom</th>
                    <th className="px-5 py-3">Prénom</th>
                    <th className="px-5 py-3">Mail</th>
                    <th className="px-5 py-3">Ligne</th>
                    <th className="px-5 py-3">Profil</th>
                    <th className="px-5 py-3">Pôle (bureau)</th>
                    <th className="px-5 py-3">Droits aux modules</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className={`hover:bg-gray-50/60 transition-colors ${estArchive(user) ? "bg-gray-50/80 text-gray-400" : ""}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2.5">
                          <span className="text-[11px] font-bold text-[#5C1A30] uppercase flex-shrink-0">
                            {user.name?.[0]}
                            {user.lastName?.[0]}
                          </span>
                          <span className="font-bold text-gray-900 uppercase">
                            {user.name?.[0]}
                            {user.lastName?.[0]}
                          </span>
                        </div>
                      </td>
                      <td className={`px-5 py-4 font-semibold uppercase ${estArchive(user) ? "text-gray-400" : "text-gray-900"}`}>
                        <span className="flex items-center gap-2">
                          {user.name}
                          {estArchive(user) && (
                            <span className="normal-case text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                              Archivé
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-gray-600">{user.lastName}</td>
                      <td className="px-5 py-4 text-gray-600">{user.email}</td>
                      <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
                        {user.phone || "—"}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {user.poste || user.role}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {POLE_LABELS[user.pole || defaultPoleForUser(user)]}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          {MODULE_KEYS.map((key) => {
                            const active = Array.isArray(user.fonctionalite)
                              ? user.fonctionalite.includes(key)
                              : user.fonctionalite === key;
                            return (
                              <div
                                key={key}
                                title={key}
                                className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  active ? "bg-[#5C1A30]" : "bg-[#f5ede8]"
                                }`}
                              >
                                {getFunctionalityIcon(key, active)}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setShowUserDetail(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-[#840040] hover:text-[#840040] transition-colors whitespace-nowrap"
                          >
                            <IoOpenOutline size={13} />
                            Ouvrir
                          </button>
                          {user.id !== utilisateur?.id && (
                            <button
                              onClick={() => handleToggleArchive(user)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-[#840040] transition-colors"
                              title={estArchive(user) ? "Réactiver l'accès" : "Archiver (collaborateur sorti)"}
                            >
                              {estArchive(user) ? <MdOutlineUnarchive size={14} /> : <MdOutlineArchive size={14} />}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 transition-colors"
                          >
                            <MdDelete size={14} />
                          </button>
                          {utilisateur?.allCabinetsAccess &&
                            user.id !== utilisateur.id &&
                            !estArchive(user) && (
                              <button
                                onClick={async () => {
                                  const confirmed = await dialog.confirm(
                                    `Se connecter en tant que ${user.name} ${user.lastName} ?`
                                  );
                                  if (confirmed) await impersonateUser(user.id);
                                }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-[#840040] transition-colors"
                                title="Se connecter en tant que"
                              >
                                <MdSwitchAccount size={14} />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showUserDetail && selectedUserId && (
        <UserDetailModal
          user={users.find((u) => u.id === selectedUserId)}
          onClose={() => setShowUserDetail(false)}
          onUserUpdated={handleUpdateUserInList}
        />
      )}

      {showCreatePage && <Create onCancel={() => setShowCreatePage(false)} />}
    </div>
  );
}

export default Admin;
